import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useProAccess } from "@/hooks/useProAccess";
import { Button } from "@/components/ui/button";

const NOTICE_KEY_PREFIX = "cg_pro_completion_notice_";

const clearCompletionNoticeMarkers = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(NOTICE_KEY_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage can be unavailable in some private browsing contexts.
  }
};

const ProAccountCompletionModal = () => {
  const { user, role, loading } = useAuth();
  const access = useProAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const markerKey = useMemo(
    () => (user ? `${NOTICE_KEY_PREFIX}${user.id}` : null),
    [user],
  );

  useEffect(() => {
    if (!user) {
      clearCompletionNoticeMarkers();
      setOpen(false);
      return;
    }

    if (
      loading ||
      role !== "pro" ||
      access.status === "loading" ||
      location.pathname === "/auth"
    ) {
      return;
    }

    if (access.status === "active") {
      setOpen(false);
      return;
    }

    if (!markerKey) return;

    try {
      if (sessionStorage.getItem(markerKey) === "shown") return;
      sessionStorage.setItem(markerKey, "shown");
    } catch {
      // If storage is unavailable, still show the notice for this render.
    }

    setOpen(true);
  }, [access.status, loading, location.pathname, markerKey, role, user]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (!open || !user || role !== "pro" || access.status === "active") return null;

  const nextStep = access.subscriptionRequired && !access.subscriptionActive
    ? {
        path: "/subscription",
        label: "Activer mon abonnement PRO",
      }
    : !access.profileCompleted
      ? {
          path: "/settings?tab=profile",
          label: "Compléter mon profil",
        }
      : access.identityVerificationStatus !== "approved"
        ? {
            path: "/settings?tab=verification",
            label: "Faire vérifier mon identité",
          }
        : {
            path: "/pro/dashboard",
            label: "Accéder à mon espace PRO",
          };

  const continueSetup = () => {
    setOpen(false);
    navigate(nextStep.path);
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/85 p-2 backdrop-blur-sm sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Finaliser votre compte professionnel"
    >
      <div className="relative flex h-[92dvh] min-h-[70dvh] w-[96vw] max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute right-2 top-2 z-10 flex h-11 w-11 items-center justify-center rounded-full border-2 border-white bg-black/75 text-white shadow-lg transition hover:scale-105 hover:bg-black focus:outline-none focus:ring-4 focus:ring-primary/40"
          aria-label="Fermer l’information"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="min-h-0 flex-1 bg-[#f7f2ff] p-1 sm:p-2">
          <img
            src="/pro-account-activation.webp"
            alt="Débloquez votre compte PRO : activez votre abonnement, complétez votre profil puis faites vérifier votre identité."
            className="h-full w-full object-contain"
          />
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-t bg-white p-3 sm:flex-row sm:items-center sm:justify-center">
          <Button
            type="button"
            onClick={continueSetup}
            size="lg"
            className="min-h-12 rounded-full px-8 text-base font-bold shadow-lg"
          >
            {nextStep.label}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setOpen(false)}
            className="min-h-11 rounded-full"
          >
            Fermer pour le moment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProAccountCompletionModal;
